/**
 * 10.9DX — POPULAÇÃO FIXA POR TERRITÓRIO. Pedido do Thiago: "cada área tem seus monstros
 * vivos fixos, sem precisar o Caçador aparecer lá pra aparecer no mapa".
 *
 * O spawner só materializa monstros de VERDADE perto do Caçador (é obrigatório: 15 territórios
 * × 14 bichos = 210 modelos 3D com IA fariam o celular derreter). O que faltava era o mundo
 * continuar POVOADO fora do alcance dele.
 *
 * Aqui cada território guarda a sua horda como PONTOS (posição + se é campeão), sem modelo,
 * sem física e sem IA — custo praticamente zero. Esses pontos:
 *   • alimentam o minimapa e o Mapa do Mundo (você vê a vida do mapa inteiro);
 *   • repõem sozinhos com o tempo quando você limpa um pedaço e vai embora.
 *
 * Quando o Caçador chega perto, o território passa a ser MATERIALIZADO: quem manda ali são os
 * inimigos reais do spawner, e os pontos virtuais daquele pedaço somem do mapa (nada de bicho
 * contado duas vezes).
 */
import type { Territory } from "../world/Territories";
import { GameConfig } from "../config/GameConfig";
import { randRange } from "../utils/math";

export interface VirtualMob {
  x: number;
  z: number;
  champion: boolean;
}

export class TerritoryPopulation {
  private readonly mobs = new Map<string, VirtualMob[]>();
  private readonly terrs = new Map<string, Territory>();
  private refill = 0;

  /** Troca de região: monta a população cheia de todos os territórios da grade. */
  setRegion(list: Territory[]): void {
    this.mobs.clear();
    this.terrs.clear();
    for (const t of list) {
      this.terrs.set(t.def.id, t);
      const arr: VirtualMob[] = [];
      const want = this.wantOf(t);
      for (let i = 0; i < want; i++) arr.push(this.makeMob(t, i === 0 && !!t.def.champion));
      this.mobs.set(t.def.id, arr);
    }
  }

  /** Quantos vivos aquele pedaço mantém (a gangue + o campeão, se tiver). */
  private wantOf(t: Territory): number {
    return GameConfig.territories.alivePerTerritory + (t.def.champion ? 1 : 0);
  }

  private makeMob(t: Territory, champion: boolean): VirtualMob {
    const mw = Math.max(0, t.halfW - 4);
    const mh = Math.max(0, t.halfH - 4);
    return { x: t.cx + randRange(-mw, mw), z: t.cz + randRange(-mh, mh), champion };
  }

  /** Reposição lenta: um pedaço limpo volta a encher sozinho enquanto você caça em outro lugar. */
  update(dt: number): void {
    const c = GameConfig.territories.population;
    this.refill -= dt;
    if (this.refill > 0) return;
    this.refill = c.refillInterval;
    for (const [id, arr] of this.mobs) {
      const t = this.terrs.get(id);
      if (!t) continue;
      const want = this.wantOf(t);
      if (arr.length >= want) continue;
      const champion = !!t.def.champion && !arr.some((m) => m.champion);
      arr.push(this.makeMob(t, champion));
    }
  }

  /**
   * O Caçador limpou um pedaço materializado: a população virtual dele passa a refletir o que
   * sobrou de verdade, senão o mapa mostraria horda cheia num lugar que você acabou de varrer.
   */
  syncMaterialized(terrId: string, aliveReal: number): void {
    const arr = this.mobs.get(terrId);
    const t = this.terrs.get(terrId);
    if (!arr || !t) return;
    const want = Math.min(this.wantOf(t), Math.max(0, aliveReal));
    if (arr.length > want) arr.length = want;
    while (arr.length < want) arr.push(this.makeMob(t, false));
  }

  /** Pontos pro mapa: só dos territórios que NÃO estão materializados (e já descobertos). */
  blips(isMaterialized: (terrId: string) => boolean, isDiscovered: (terrId: string) => boolean, out: VirtualMob[]): VirtualMob[] {
    for (const [id, arr] of this.mobs) {
      if (isMaterialized(id) || !isDiscovered(id)) continue;
      // 10.9FE — com 50 por territorio o mapa virava um borrao: desenha no maximo 8 por pedaco
      for (let i = 0; i < arr.length && i < 8; i++) out.push(arr[i]);
    }
    return out;
  }
}
